// NavBar.js - Responsive navigation bar with logo, theme switcher, language dropdown (EN/ES options, defaults to 'en' for new users via global context), and dynamic button based on route.
// Updated: Added route detection using useLocation to show history/new invoice button only on home route ("/"). On other routes (e.g., "/terms-of-service", "/privacy-policy"), shows a back button instead.
// Updated: Changed Navbar.Brand to use Link for proper navigation to home route ("/") instead of ineffective hash link.
// Fixes: Z-index on menu for layering; position relative and align="end" for layout; explicit padding on toggle for button height consistency.
// New Fix: Added 'navbar-btn' class to all buttons and dropdown toggle for uniform height (38px) and padding, ensuring consistency across desktop/tablet/mobile resolutions.
// Updated: "New Invoice" button now calls createNewInvoice() from global context for proper numbering and flag management.
// Uses global context for language, theme, and invoice clearing. Now route-aware for better UX on policy pages.
import React from 'react';
import { Navbar, Nav, Container, Button, Dropdown } from 'react-bootstrap'; // Added Dropdown for language selection
import { FaSun, FaMoon } from 'react-icons/fa'; // Icons for theme toggle
import { MdTranslate } from 'react-icons/md'; // Icon for language dropdown toggle (Google Translate-like)
import { useLocation, Link } from 'react-router-dom'; // Added useLocation for route detection and Link for back button and brand navigation
import { useGlobalContext } from '../hooks/useGlobalContext'; // Access createNewInvoice, theme, toggleTheme, language, and changeLanguage
import { useTranslation } from 'react-i18next';

// NavBar component: Responsive bar with logo, language dropdown (EN/ES, Bootstrap collapse), theme switch, and route-aware button (history/new invoice on home, back on others).
// Fixes: Dropdown z-index, layout shifts, and button height consistency (padding matches .btn class). Now includes uniform height via 'navbar-btn' class for all interactive elements.
const NavBar = ({ currentView, setCurrentView }) => {
  const { createNewInvoice, theme, toggleTheme, language, changeLanguage } = useGlobalContext(); // Updated: Use createNewInvoice instead of clearCurrentInvoice
  const { t } = useTranslation();
  const location = useLocation(); // Hook to get current route pathname for conditional rendering

  return (
    <Navbar bg={theme === 'dark' ? 'dark' : 'light'} expand="lg"> {/* Conditional bg based on theme */}
      <Container fluid> {/* Fluid container for full-width responsiveness */}
        {/* Logo/Brand: Left-aligned, now navigates to home route ("/") using Link */}
        <Navbar.Brand as={Link} to="/">Invoice Generator</Navbar.Brand>
        
        {/* Toggle button for mobile collapse */}
        <Navbar.Toggle aria-controls="basic-navbar-nav" />
        
        {/* Collapsible nav items: Responsive dropdown and buttons collapse on smaller screens */}
        <Navbar.Collapse id="basic-navbar-nav">
          <Nav className="ms-auto"> {/* ms-auto aligns items to the right */}
            {/* Language Dropdown: Responsive dropdown with MdTranslate icon toggle; options for EN and ES, with active state for current language */}
            <Dropdown className="me-2" style={{ position: 'relative' }}>
              <Dropdown.Toggle
                variant="outline-info"
                id="language-dropdown"
                className="navbar-btn d-flex align-items-center"  // Added 'navbar-btn' for uniform height and padding
                aria-label="Select language"
              >
                <MdTranslate />
              </Dropdown.Toggle>
              <Dropdown.Menu align="end" style={{ zIndex: 1050 }}>
                <Dropdown.Item
                  onClick={() => changeLanguage('en')}
                  active={language === 'en'}
                >
                  EN
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => changeLanguage('es')}
                  active={language === 'es'}
                >
                  ES
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            
            {/* Switch Theme Button: Icon button showing FaSun for light theme, FaMoon for dark theme; toggles on click */}
            <Button
              variant="outline-secondary"
              className="me-2 navbar-btn d-flex align-items-center"  // Added 'navbar-btn' for uniform height and padding
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} theme`}
            >
              {theme === 'light' ? <FaSun /> : <FaMoon />}
            </Button>
               
            {/* Dynamic Button: Conditionally render based on route */}
            {location.pathname === '/' ? (
              // History/New Invoice Button: Toggles between 'form' and 'history' views (only on home route)
              currentView === 'form' ? (
                <Button 
                  variant="outline-primary" 
                  className="navbar-btn"  // Added 'navbar-btn' for uniform height and padding
                  onClick={() => setCurrentView('history')}
                >
                  {t('history')}
                </Button>
              ) : (
                <Button 
                  variant="outline-primary" 
                  className="navbar-btn"  // Added 'navbar-btn' for uniform height and padding
                  onClick={() => { createNewInvoice(); setCurrentView('form'); }}  // Updated: Call createNewInvoice() for proper numbering
                >
                  {t('newInvoice')}
                </Button>
              )
            ) : (
              // Back Button: Navigates to home route (invoice form) on non-home routes (e.g., terms, privacy)
              <Button 
                as={Link} 
                to="/" 
                variant="outline-primary" 
                className="navbar-btn"  // Added 'navbar-btn' for uniform height and padding
              >
                {t('backToHome')}
              </Button>
            )}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default NavBar;